'use client';

import { useCallback, useState } from 'react';
import { pendingIdForIndex } from '@/components/ai-review/compact/pending-id';

/**
 * What the reviewer has turned down on an AI-extract review (VALE-1208).
 *
 * **One store, shared by both review surfaces**, so the in-matter import and
 * matter creation behave identically rather than each growing its own rules.
 * They ended up with two implementations once, the import surface expressing a
 * reject through the card path's `acceptedChanges`, matter creation through
 * local state and a submit-time filter, and the two immediately disagreed
 * about what a reject even did on a created contact.
 *
 * Both keys are the row's synthetic id (`pendingIdForIndex`), which is what the
 * list, the drawer and the detail block all key by already.
 */
export interface ReviewRejections {
  /** Contacts that will not be created or updated. */
  contacts: Record<number, boolean>;
  /** Fields, per contact, that will not be written. */
  fields: Record<number, Record<string, boolean>>;
  /** Inventory items that will not be created or updated (VALE-1181). */
  inventoryItems: Record<number, boolean>;
  /** Fields, per inventory item, that will not be written (VALE-1181). */
  inventoryFields: Record<number, Record<string, boolean>>;
  /**
   * **What a rejected field clears TO, when the row's own editor knows better
   * than the payload does** (VALE-1375).
   *
   * `withoutRejectedFields` otherwise infers the cleared value from the
   * payload's runtime type, and for a flag that gives `false` — right for a
   * plain boolean, wrong for a TRI-STATE. `isProvingLpr` is the live case: its
   * spec exists precisely because a flat `No` there is "a determination nobody
   * had made", and committing one writes `nonProvingReason: 'leave_reserved'`,
   * inventing a reason for a decision that was never taken. Unanswered is
   * `null`, and nothing in the payload distinguishes the two — only the editor
   * kind does, and that lives on the spec.
   *
   * So the surface that offers the reject says what clearing means for that
   * row, which is the model this ticket settled: one definition of the
   * behaviour, supplied by the row rather than guessed at the edge.
   */
  fieldClearValues: Record<number, Record<string, unknown>>;
  inventoryFieldClearValues: Record<number, Record<string, unknown>>;
  /**
   * Deceased / matter fields that will not be written (VALE-1183).
   *
   * **Un-keyed, because there is one deceased.** Both other halves of a review
   * are lists, so their maps are keyed by the row's synthetic id; the matter is
   * a single record on both surfaces, so a row key here would be a constant
   * dressed up as an index. The inner keys are the **diff's** own dot-notation
   * (`firstName`, `address.suburb`) on both surfaces, which is what lets the
   * import surface hand them straight to `buildMergedProposed`.
   */
  matterFields: Record<string, boolean>;
}

/**
 * **Inventory gets its own two maps rather than sharing the contact ones**
 * (VALE-1181), and this is a correctness requirement, not tidiness.
 *
 * Both halves of a review key their rows by `pendingIdForIndex`, which encodes
 * *position in the preview array* — so the first contact and the first
 * inventory item are both `-1`. One shared map would have a reviewer rejecting
 * an asset's balance and silently un-ticking a beneficiary, with nothing on
 * screen connecting the two.
 */
export const NO_REJECTIONS: ReviewRejections = {
  contacts: {},
  fields: {},
  inventoryItems: {},
  inventoryFields: {},
  fieldClearValues: {},
  inventoryFieldClearValues: {},
  matterFields: {}
};

export interface ReviewRejectionsApi extends ReviewRejections {
  toggleContact: (contactId: number) => void;
  /**
   * `clearTo` is the value this field becomes once rejected, when the row's
   * editor knows it (VALE-1375) — see {@link ReviewRejections.fieldClearValues}.
   * Omit and the apply path infers it from the payload.
   */
  toggleField: (contactId: number, key: string, clearTo?: unknown) => void;
  /** Whether this contact is rejected. */
  isContactRejected: (contactId: number) => boolean;
  /** The reject flags for one contact, in `acceptedChanges` shape. */
  fieldsFor: (contactId: number) => Record<string, boolean>;
  /** The cleared values this contact's rejects declared. */
  clearValuesFor: (contactId: number) => Record<string, unknown>;
  /** The inventory twins of the four above (VALE-1181). */
  toggleInventoryItem: (itemId: number) => void;
  toggleInventoryField: (
    itemId: number,
    key: string,
    clearTo?: unknown
  ) => void;
  isInventoryItemRejected: (itemId: number) => boolean;
  inventoryFieldsFor: (itemId: number) => Record<string, boolean>;
  inventoryClearValuesFor: (itemId: number) => Record<string, unknown>;
  /**
   * **The deceased's fields — one gesture over a KEY SET, not a key**
   * (VALE-1183).
   *
   * The other two halves toggle a single key because a row and a diff key are
   * one thing there. The deceased's address is not: the diff splits it across
   * five `address.*` keys while the review draws it as one row, so a reject on
   * that row has to turn down every part the AI touched or the apply writes the
   * half the reader thought they had declined. The caller hands in the whole
   * set, and every member moves together — including the row's own base key,
   * which is what the treatment reads back to decide whether the row draws as
   * rejected.
   *
   * The set's *membership* is derived from the diff by
   * `deceasedRejectKeySet`; this only guarantees the members stay in step.
   */
  toggleMatterFields: (keys: readonly string[]) => void;
  /** The matter's reject flags, in `acceptedChanges` shape. */
  matterFieldsFor: () => Record<string, boolean>;
  /**
   * **Shift every key above a removed row down one** (VALE-1617).
   *
   * Both halves key by `pendingIdForIndex`, which encodes *position in the
   * preview array* — and deleting a row filters that array, so every row after
   * it moves. Nothing remapped this state, so after one delete each remaining
   * row read the state of the row that used to sit above it: a reject someone
   * made on Carol was shown against Bob, and a flag the edits store said Bob
   * had answered made Carol's control render as though it had been answered
   * too. Toggling `Beneficiary` then appeared to do nothing on the row clicked
   * while the row above changed, which is how it was reported.
   *
   * The commit path never had this problem — it resolves a contact by `tempId`
   * — which is why the values stayed correct and only the review's own marking
   * lied. `keptRowsAndRemap` does this same arithmetic at submit; this is the
   * live half it was missing.
   *
   * `which` because contacts and inventory hold separate maps: they both mint
   * `-1` for their first row, and one shared remap would have an asset's
   * deletion shift a beneficiary's rejects.
   */
  remapAfterRemoval: (which: 'contacts' | 'inventory', index: number) => void;
}

/**
 * Hold the review's rejections.
 *
 * **A reject marks; it never destroys.** The value stays in the record and the
 * row stays in the list, greyed, so the reviewer can see what they turned
 * down and hand it back. Removing the row or clearing the value would leave
 * nothing for an undo to restore, which is the whole reason this state exists
 * rather than a mutation.
 *
 * **The host owns honouring it at apply**, because only the host knows the
 * shape of its own payload: the import surface reverts an update through
 * `buildMergedProposed` and strips a create's rejected keys, matter creation
 * drops them as it builds its submit. What must not differ, and what living
 * here guarantees, is what the reviewer *sees* and *can undo*.
 */
export function useReviewRejections(): ReviewRejectionsApi {
  const [rejections, setRejections] = useState<ReviewRejections>(NO_REJECTIONS);

  const toggleContact = useCallback((contactId: number) => {
    setRejections((current) => ({
      ...current,
      contacts: {
        ...current.contacts,
        [contactId]: !current.contacts[contactId]
      }
    }));
  }, []);

  const toggleField = useCallback(
    (contactId: number, key: string, clearTo?: unknown) => {
      setRejections((current) => {
        const row = current.fields[contactId] ?? {};
        /* `false` means rejected, so the NEXT accepted flag says which way
           this toggle is going. */
        const nowRejected = !(row[key] ?? true) === false;
        return {
          ...current,
          fields: {
            ...current.fields,
            /*
              `false` means rejected, matching the `acceptedChanges` contract the
              apply payload already speaks, so a host can hand this straight to
              `buildMergedProposed` without translating it first.
            */
            [contactId]: { ...row, [key]: !(row[key] ?? true) }
          },
          fieldClearValues: withClearValue(
            current.fieldClearValues,
            contactId,
            key,
            clearTo,
            nowRejected
          )
        };
      });
    },
    []
  );

  const isContactRejected = useCallback(
    (contactId: number) => !!rejections.contacts[contactId],
    [rejections.contacts]
  );

  const fieldsFor = useCallback(
    (contactId: number) => rejections.fields[contactId] ?? {},
    [rejections.fields]
  );

  const clearValuesFor = useCallback(
    (contactId: number) => rejections.fieldClearValues[contactId] ?? {},
    [rejections.fieldClearValues]
  );

  const toggleInventoryItem = useCallback((itemId: number) => {
    setRejections((current) => ({
      ...current,
      inventoryItems: {
        ...current.inventoryItems,
        [itemId]: !current.inventoryItems[itemId]
      }
    }));
  }, []);

  const toggleInventoryField = useCallback(
    (itemId: number, key: string, clearTo?: unknown) => {
      setRejections((current) => {
        const row = current.inventoryFields[itemId] ?? {};
        /* See the contacts twin: the next accepted flag says which way. */
        const nowRejected = !(row[key] ?? true) === false;
        return {
          ...current,
          inventoryFields: {
            ...current.inventoryFields,
            [itemId]: { ...row, [key]: !(row[key] ?? true) }
          },
          inventoryFieldClearValues: withClearValue(
            current.inventoryFieldClearValues,
            itemId,
            key,
            clearTo,
            nowRejected
          )
        };
      });
    },
    []
  );

  const isInventoryItemRejected = useCallback(
    (itemId: number) => !!rejections.inventoryItems[itemId],
    [rejections.inventoryItems]
  );

  const inventoryFieldsFor = useCallback(
    (itemId: number) => rejections.inventoryFields[itemId] ?? {},
    [rejections.inventoryFields]
  );

  const inventoryClearValuesFor = useCallback(
    (itemId: number) => rejections.inventoryFieldClearValues[itemId] ?? {},
    [rejections.inventoryFieldClearValues]
  );

  const toggleMatterFields = useCallback((keys: readonly string[]) => {
    if (keys.length === 0) return;
    setRejections((current) => {
      /*
        The FIRST key decides the direction and every member follows it, rather
        than each flipping its own flag. A set half-rejected — which is what
        per-key flipping produces the moment one member was toggled on its own —
        would leave the row drawing as rejected while part of the value still
        applied.
      */
      const rejecting = current.matterFields[keys[0]] !== false;
      const next = { ...current.matterFields };
      for (const key of keys) next[key] = !rejecting;
      return { ...current, matterFields: next };
    });
  }, []);

  const matterFieldsFor = useCallback(
    () => rejections.matterFields,
    [rejections.matterFields]
  );

  const remapAfterRemoval = useCallback(
    (which: 'contacts' | 'inventory', index: number) => {
      const removedId = pendingIdForIndex(index);
      setRejections((current) => {
        const itemsKey = which === 'contacts' ? 'contacts' : 'inventoryItems';
        const fieldsKey = which === 'contacts' ? 'fields' : 'inventoryFields';
        const clearKey =
          which === 'contacts'
            ? 'fieldClearValues'
            : 'inventoryFieldClearValues';
        return {
          ...current,
          [itemsKey]: shiftRowKeys(current[itemsKey], removedId),
          [fieldsKey]: shiftRowKeys(current[fieldsKey], removedId),
          [clearKey]: shiftRowKeys(current[clearKey], removedId)
        };
      });
    },
    []
  );

  return {
    ...rejections,
    remapAfterRemoval,
    toggleContact,
    toggleField,
    isContactRejected,
    fieldsFor,
    clearValuesFor,
    toggleInventoryItem,
    toggleInventoryField,
    isInventoryItemRejected,
    inventoryFieldsFor,
    inventoryClearValuesFor,
    toggleMatterFields,
    matterFieldsFor
  };
}

/**
 * **Re-key one map after the row at `removedId` was deleted** (VALE-1617).
 *
 * Ids are `pendingIdForIndex(index)` — `-(index + 1)` — so a LOWER id is a
 * LATER row. Deleting index *i* moves every row after it one place earlier,
 * which means every key strictly below `removedId` moves one step *up* toward
 * zero. The removed row's own entry is dropped: there is no row left for it to
 * describe, and leaving it would be inherited by whoever next lands on that id.
 *
 * Rows above the deletion keep their keys untouched, which is why this is a
 * shift rather than a rebuild.
 */
function shiftRowKeys<T>(
  map: Record<number, T>,
  removedId: number
): Record<number, T> {
  const next: Record<number, T> = {};
  for (const [raw, value] of Object.entries(map)) {
    const id = Number(raw);
    if (id === removedId) continue;
    next[id < removedId ? id + 1 : id] = value;
  }
  return next;
}

/**
 * Record (or drop) the value a rejected field clears to.
 *
 * Kept only while the field IS rejected: un-rejecting must leave nothing
 * behind, or a later re-reject would silently inherit a stale answer.
 */
function withClearValue(
  current: Record<number, Record<string, unknown>>,
  rowId: number,
  key: string,
  clearTo: unknown,
  nowRejected: boolean
): Record<number, Record<string, unknown>> {
  const row = { ...(current[rowId] ?? {}) };
  if (nowRejected && clearTo !== undefined) row[key] = clearTo;
  else delete row[key];
  return { ...current, [rowId]: row };
}

/** The keys this contact has rejected, the shape apply payloads consume. */
export function rejectedKeysFor(
  rejections: ReviewRejections,
  contactId: number
): string[] {
  const row = rejections.fields[contactId] ?? {};
  return Object.keys(row).filter((key) => row[key] === false);
}

/** The keys this inventory item has rejected (VALE-1181). */
export function rejectedInventoryKeysFor(
  rejections: ReviewRejections,
  itemId: number
): string[] {
  const row = rejections.inventoryFields[itemId] ?? {};
  return Object.keys(row).filter((key) => row[key] === false);
}

/** The deceased / matter keys the reviewer has turned down (VALE-1183). */
export function rejectedMatterKeysFor(rejections: ReviewRejections): string[] {
  return Object.keys(rejections.matterFields).filter(
    (key) => rejections.matterFields[key] === false
  );
}
